import { Component, computed } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { APP_ROUTES, MenuItem, RoleEnum, SIDEBAR_MENU } from '@core/constants';
import { AuthService, ConfirmService, Global, ToastService } from '@core/services';
import { Header, Footer } from '@shared/components';

@Component({
  selector: 'app-layout',
  imports: [Header, Footer, RouterOutlet, RouterModule],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  menuOpen = false;
  sidebarCollapsed = false;
  adminOpen = true;
  isLoggingOut = false;

  readonly sidebarUserName = computed(
    () => this.global.userName() || this.global.user() || 'Usuario',
  );
  readonly sidebarUserRole = computed(
    () => this.global.rolDesc() || this.global.rol()?.[0] || 'Sin rol asignado',
  );

  readonly dynamicMenu = computed<MenuItem[]>(() => {
    const userRoles = this.global.rol() || [];

    return SIDEBAR_MENU.filter((item) => this.checkAccess(item.roles, userRoles)).map(
      (item) => {
        if (item.subItems) {
          return {
            ...item,
            subItems: item.subItems.filter((sub) =>
              this.checkAccess(sub.roles, userRoles),
            ),
          };
        }
        return item;
      },
    );
  });

  constructor(
    private readonly authService: AuthService,
    private readonly confirmService: ConfirmService,
    private readonly toastService: ToastService,
    private readonly global: Global,
    private readonly router: Router,
  ) {}

  toggleMenu(): void {
    if (this.isMobileViewport()) {
      this.menuOpen = !this.menuOpen;
      return;
    }

    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeMenuOnMobile(): void {
    if (this.isMobileViewport()) {
      this.menuOpen = false;
    }
  }

  toggleAdmin(): void {
    this.adminOpen = !this.adminOpen;
  }

  async onLogout(): Promise<void> {
    if (this.isLoggingOut) return;

    const accepted = await this.confirmService.ask({
      title: 'Cerrar sesión',
      message: '¿Deseas cerrar la sesión actual?',
      confirmText: 'Cerrar sesión',
      cancelText: 'Cancelar',
    });

    if (!accepted) return;

    this.isLoggingOut = true;
    try {
      await this.authService.logout();
    } catch {
      this.toastService.show(
        'No se pudo notificar el logout al servidor.',
        2500,
        'warning',
      );
    } finally {
      this.authService.clearSession();
      this.menuOpen = false;
      this.sidebarCollapsed = false;
      this.adminOpen = false;
      this.isLoggingOut = false;
      await this.router.navigate([APP_ROUTES.LOGIN]);
    }
  }

  private checkAccess(allowedRoles: RoleEnum[], userRoles: string | string[]): boolean {
    const currentRoles = Array.isArray(userRoles) ? userRoles : [userRoles];
    return allowedRoles.some((role) => currentRoles.includes(role));
  }

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 920px)').matches;
  }
}
